import { useState } from 'react'
import { Calendar as CalendarIcon, CalendarDays, CalendarRange, Phone, TrendingUp, CheckCircle2, PhoneMissed, ChevronDown } from 'lucide-react'
import { format } from 'date-fns'
import type { DateRange } from 'react-day-picker'

import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'

import { BonusProgressV2 } from './components/BonusProgressV2'
import { StatusCard } from './components/StatusCard'
import { Leaderboard } from './components/Leaderboard'
import { ActivityFeed } from './components/ActivityFeed'
import { PerformanceTable } from './components/PerformanceTable'
import { TeamFocusV2 } from './components/TeamFocusV2'
import { DashboardSkeleton } from './components/DashboardSkeleton'
import type { TeamPerformanceData, Period } from './types'
import { CONVERSION_THRESHOLDS, QUALITY_THRESHOLDS } from './types'
import type { StatusLevel } from '@/styles/design-tokens'

interface TeamPerformanceDashboardV2Props {
  initialData: TeamPerformanceData
}

function getConversionStatus(rate: number): { level: StatusLevel; label: string } {
  if (rate >= CONVERSION_THRESHOLDS.ELITE) return { level: 'elite', label: 'Elite' }
  if (rate >= CONVERSION_THRESHOLDS.STRONG) return { level: 'strong', label: 'Strong' }
  if (rate >= CONVERSION_THRESHOLDS.APPROACHING) return { level: 'approaching', label: 'Approaching' }
  return { level: 'neutral', label: 'Building' }
}

function getQualityStatus(score: number): { level: StatusLevel; label: string } {
  if (score >= QUALITY_THRESHOLDS.EXCELLENT) return { level: 'elite', label: 'Excellent' }
  if (score >= QUALITY_THRESHOLDS.STRONG) return { level: 'strong', label: 'Strong' }
  if (score >= QUALITY_THRESHOLDS.ACCEPTABLE) return { level: 'approaching', label: 'Acceptable' }
  return { level: 'danger', label: 'Needs Work' }
}

function getMissedCallsStatus(count: number, target: number = 15): { level: StatusLevel; label: string } {
  if (count >= target) return { level: 'danger', label: 'Over Target!' }
  if (count >= target * 0.8) return { level: 'approaching', label: 'Near Limit' }
  return { level: 'strong', label: 'On Track' }
}

export function TeamPerformanceDashboardV2({ initialData }: TeamPerformanceDashboardV2Props) {
  const [period, setPeriod] = useState<Period>(initialData.period || 'month')
  const [data] = useState(initialData)
  const [isLoading, setIsLoading] = useState(false)
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    if (initialData.startDate && initialData.endDate) {
      return {
        from: new Date(initialData.startDate),
        to: new Date(initialData.endDate)
      }
    }
    return undefined
  })

  const navigateTo = (newPeriod: 'today' | 'month') => {
    setIsLoading(true)
    window.location.href = `/dashboard/team?period=${newPeriod}`
  }

  const handleDateRangeSelect = (range: DateRange | undefined) => {
    setDateRange(range)
    if (range?.from && range?.to) {
      const startDate = format(range.from, 'yyyy-MM-dd')
      const endDate = format(range.to, 'yyyy-MM-dd')
      setIsLoading(true)
      window.location.href = `/dashboard/team?period=custom&start_date=${startDate}&end_date=${endDate}`
    }
  }

  const metrics = data.teamMetrics
  const isToday = period === 'today'

  const conversionStatus = getConversionStatus(metrics.conversionRate)
  const qualityStatus = getQualityStatus(metrics.qualityScoreMonth)
  const missedStatus = getMissedCallsStatus(metrics.missedCallsMonth)

  if (isLoading) {
    return <DashboardSkeleton />
  }

  return (
    <div className="space-y-8">
      {/* Period Toggle - Clean Style with Integrated Calendar */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-1 bg-zinc-100 p-1 rounded-lg">
          {/* Today button */}
          <button
            onClick={() => navigateTo('today')}
            className={cn(
              "px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2",
              period === 'today' ? "bg-white shadow-sm" : "hover:bg-white/50"
            )}
          >
            <CalendarIcon className="w-4 h-4" />
            Today
          </button>

          {/* This Month button */}
          <button
            onClick={() => navigateTo('month')}
            className={cn(
              "px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2",
              period === 'month' ? "bg-white shadow-sm" : "hover:bg-white/50"
            )}
          >
            <CalendarDays className="w-4 h-4" />
            This Month
          </button>

          {/* Custom - Popover с Calendar */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                className={cn(
                  "px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2",
                  period === 'custom' ? "bg-white shadow-sm" : "hover:bg-white/50"
                )}
              >
                <CalendarRange className="w-4 h-4" />
                {dateRange?.from && dateRange?.to
                  ? `${format(dateRange.from, 'dd.MM')} — ${format(dateRange.to, 'dd.MM')}`
                  : 'Custom'}
                <ChevronDown className="w-3 h-3 opacity-50" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={handleDateRangeSelect}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* V2 Badge */}
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-medium border border-blue-200">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
          V2 Light Theme
        </span>
      </div>

      {/* Hero: Bonus Progress - V2 Light Version */}
      <BonusProgressV2 bonus={data.bonusProgress} />

      {/* Metric Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Calls */}
        <StatusCard
          title="Total Calls"
          value={isToday ? metrics.totalCallsToday : metrics.totalCallsMonth}
          subtitle={isToday ? 'Today' : 'This Month'}
          secondary={!isToday ? `Today: ${metrics.totalCallsToday}` : undefined}
          status="neutral"
          icon={<Phone className="w-6 h-6" />}
        />

        {/* Conversion */}
        <StatusCard
          title="Team Conversion"
          value={metrics.conversionRate.toFixed(1)}
          unit="%"
          subtitle={`Today: ${metrics.conversionRateToday.toFixed(1)}%`}
          secondary="Target: 55%"
          status={conversionStatus.level}
          statusLabel={conversionStatus.label}
          progress={{ value: metrics.conversionRate, max: 55 }}
          icon={<TrendingUp className="w-6 h-6" />}
        />

        {/* Quality */}
        <StatusCard
          title="Quality Score"
          value={metrics.qualityScoreMonth}
          subtitle={`Today: ${metrics.qualityScoreToday}`}
          secondary="Target: 80+"
          status={qualityStatus.level}
          statusLabel={qualityStatus.label}
          progress={{ value: metrics.qualityScoreMonth, max: 100 }}
          icon={<CheckCircle2 className="w-6 h-6" />}
        />

        {/* Missed Calls */}
        <StatusCard
          title="Missed Calls"
          value={metrics.missedCallsMonth}
          subtitle={`Today: ${metrics.missedCallsToday}`}
          secondary="Target: <15/month"
          status={missedStatus.level}
          statusLabel={missedStatus.label}
          progress={{ value: metrics.missedCallsMonth, max: 15 }}
          icon={<PhoneMissed className="w-6 h-6" />}
          inverted
        />
      </div>

      {/* Leaderboards and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top Conversion */}
        <Leaderboard
          title="Top Conversion"
          items={data.topConversion}
          metricSuffix="%"
          icon={<TrendingUp className="w-5 h-5" />}
          accentColor="emerald"
        />

        {/* Top Quality */}
        <Leaderboard
          title="Top Quality"
          items={data.topQuality}
          metricSuffix=""
          icon={<CheckCircle2 className="w-5 h-5" />}
          accentColor="blue"
        />

        {/* Activity Feed */}
        <ActivityFeed activities={data.recentActivities} />
      </div>

      {/* Performance Table */}
      <PerformanceTable operators={data.operators} />

      {/* Team Focus - V2 Light Version */}
      <TeamFocusV2 />
    </div>
  )
}
